import {
    Component,
    Inject,
    OnDestroy,
    OnInit,
    PLATFORM_ID,
} from "@angular/core";
import { Meta, Title } from "@angular/platform-browser";
import { ActivatedRoute, Router } from "@angular/router";
import { combineLatest as observableCombineLatest, Subscriber } from "rxjs";

import { BlogService } from "../../shared/blog.service";
import { Blog, BlogCategory } from "../../shared/model/blogs";

@Component({
    selector: "mlp-blog-category",
    templateUrl: "./blog-category.component.html",
    styleUrls: ["./blog-category.component.scss"],
})
export class BlogCategoryComponent implements OnInit, OnDestroy {
    page = 1;
    selectedCategory: BlogCategory;
    blogPosts: Array<Blog> = [];
    notFound = false;

    private init = true;
    private subscribed: Subscriber<any>;

    constructor(
        @Inject(PLATFORM_ID) private platformId,
        public blogService: BlogService,
        private activatedRoute: ActivatedRoute,
        private router: Router,
        private meta: Meta,
        private title: Title
    ) {}

    ngOnInit(): void {
        // subscribe to param changes (category) and query params (page)
        observableCombineLatest(
            this.activatedRoute.params,
            this.activatedRoute.queryParams,
            (params, qparams) => ({ params, qparams })
        ).subscribe((_) => {
            this.processParamsChanges();
        });

        setTimeout(() => {
            this.init = false;
        }, 2000);
    }

    ngOnDestroy(): void {
        if (this.subscribed) {
            this.subscribed.unsubscribe();
        }
    }

    pageChange(): void {
        if (!this.init) {
            this.router.navigate(
                [
                    "/blog/category",
                    this.activatedRoute.snapshot.params.category,
                ],
                {
                    queryParams: {
                        page: this.page,
                    },
                }
            );
        }
    }

    private processParamsChanges(): void {
        const categorySlug: string =
            this.activatedRoute.snapshot.params.category;
        this.page = this.activatedRoute.snapshot.queryParams.page
            ? Number(this.activatedRoute.snapshot.queryParams.page)
            : 1;

        this.notFound = false;
        if (this.blogService.categories.length > 0) {
            this.setCategoryBlogFromSlug(categorySlug);
            this.getBlogPostsFromCategoryId(this.selectedCategory.id);
            this.updateMetaData(this.selectedCategory);
        } else {
            this.subscribed = this.blogService.categoriesLoadedEvent.subscribe(
                () => {
                    this.setCategoryBlogFromSlug(categorySlug);
                    if (this.selectedCategory) {
                        this.getBlogPostsFromCategoryId(
                            this.selectedCategory.id
                        );
                        this.updateMetaData(this.selectedCategory);
                    } else {
                        this.notFound = true;
                    }
                }
            );
        }
    }

    private getBlogPostsFromCategoryId(categoryId: number): void {
        this.blogService
            .getBlogFromCategory(
                categoryId,
                this.page,
                this.blogService.blogsLatestPerPage
            )
            .subscribe((data) => {
                this.blogPosts = data;
            });
    }

    private setCategoryBlogFromSlug(categorySlug: string): void {
        this.blogService.categories.forEach((category) => {
            if (category.slug === categorySlug) {
                this.selectedCategory = category;
            }
        });
    }

    private updateMetaData(blogCategory: BlogCategory): void {
        const title = `Playfinder Blog | ${blogCategory.name}`;
        const metaDescription = blogCategory.description
            .replace(/<\/?[^>]+(>|$)/g, "") // remove tags
            .replace(/\r?\n|\r/g, " "); // replace new lines with spaces
        if (this.title.getTitle() !== title) {
            this.title.setTitle(title);
            this.meta.updateTag({ property: "og:title", content: title });
            this.meta.updateTag({ property: "twitter:title", content: title });
        }

        this.meta.updateTag({ name: "description", content: metaDescription });
        this.meta.updateTag({
            property: "og:description",
            content: metaDescription,
        });
    }
}
